import React, { useState, useEffect } from 'react';
import { recordPaymentWithBalance, getPaymentAnalytics } from '../firebase/paymentManager';
import { getCurrentUser } from '../firebase/auth';
import './PaymentForm.css';

function EnhancedPaymentForm({ tenantId, member, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    amount: '',
    paymentMethod: 'cash',
    notes: '',
    paymentType: 'regular'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [paymentAnalytics, setPaymentAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);

  useEffect(() => {
    if (member) {
      loadPaymentAnalytics();
      // Set default amount to remaining balance if any
      if (member.currentBalance && member.currentBalance > 0) {
        setFormData(prev => ({
          ...prev,
          amount: member.currentBalance.toString()
        }));
      } else if (member.fees) {
        // Extract numeric value from fees string
        const numericValue = parseFloat(member.fees.replace(/[^\d.]/g, ''));
        if (!isNaN(numericValue)) {
          setFormData(prev => ({
            ...prev,
            amount: numericValue.toString()
          }));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member]);

  const loadPaymentAnalytics = async () => {
    if (!tenantId || !member?.id) return;
    setLoadingAnalytics(true);
    try {
      const result = await getPaymentAnalytics(tenantId, member.id);
      if (result.success) {
        setPaymentAnalytics(result.analytics);
      }
    } catch (error) {
      console.error('Error loading payment analytics:', error);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setError('Please enter a valid payment amount');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const paymentData = {
        amount: parseFloat(formData.amount),
        paymentMethod: formData.paymentMethod,
        notes: formData.notes,
        paymentType: formData.paymentType
      };

      const currentUser = getCurrentUser();
      if (!currentUser) {
        setError('You must be logged in to record payments');
        return;
      }

      const result = await recordPaymentWithBalance(tenantId, member.id, paymentData);
      
      if (result.success) {
        setSuccess(result.message);
        // Reload analytics to show updated balance
        await loadPaymentAnalytics();
        
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      } else {
        setError(result.message || 'Failed to record payment');
      }
    } catch (error) {
      console.error('Payment recording error:', error);
      setError(`An error occurred: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDaysUntilDue = () => {
    if (!member.nextDueDate) return null;
    
    const dueDate = new Date(member.nextDueDate);
    const today = new Date();
    const diffTime = dueDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  const getPaymentStatus = () => {
    if (!member.nextDueDate) return 'No due date set';
    
    const daysUntilDue = getDaysUntilDue();
    
    if (daysUntilDue < 0) {
      return 'Overdue';
    } else if (daysUntilDue <= 7) {
      return 'Due Soon';
    } else {
      return 'Up to Date';
    }
  };

  const getStatusColor = () => {
    const daysUntilDue = getDaysUntilDue();
    
    if (daysUntilDue < 0) return '#e53e3e'; // Red for overdue
    if (daysUntilDue <= 7) return '#d69e2e'; // Yellow for due soon
    return '#38a169'; // Green for up to date
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toLocaleString('en-IN')}`;
  };

  const getBalanceStatus = () => {
    const balance = member.currentBalance || 0;
    if (balance === 0) return 'No Balance Due';
    if (balance > 0) return `Balance Due: ${formatCurrency(balance)}`;
    return 'Credit Balance';
  };

  return (
    <div className="payment-form-overlay" onClick={onClose}>
      <div className="payment-form-modal enhanced-payment-modal" onClick={(e) => e.stopPropagation()}>
        <div className="payment-form-header">
          <h2>Enhanced Payment Management</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="payment-form-content">
          {/* Member Information */}
          <div className="member-info-section">
            <div className="member-avatar">
              {member.fullName ? member.fullName.charAt(0).toUpperCase() : '?'}
            </div>
            <div className="member-details">
              <h3>{member.fullName || 'Unknown Member'}</h3>
              <p className="member-id">ID: {member.membershipId || member.id}</p>
              <p className="membership-info">
                {member.membershipLevel} • {member.membershipDuration} • {member.sex ? member.sex.charAt(0).toUpperCase() + member.sex.slice(1) : 'N/A'}
              </p>
            </div>
          </div>

          {/* Payment Status and Balance */}
          <div className="payment-status-section enhanced">
            <div className="status-indicator" style={{ backgroundColor: getStatusColor() }}>
              <span className="status-text">{getPaymentStatus()}</span>
            </div>
            
            <div className="balance-info">
              <div className="balance-item">
                <span className="label">Monthly Fee:</span>
                <span className="value">{formatCurrency(member.fees)}</span>
              </div>
              <div className="balance-item">
                <span className="label">Current Balance:</span>
                <span className={`value ${(member.currentBalance || 0) > 0 ? 'balance-due' : 'no-balance'}`}>
                  {formatCurrency(member.currentBalance || 0)}
                </span>
              </div>
              <div className="balance-item">
                <span className="label">Status:</span>
                <span className="value">{getBalanceStatus()}</span>
              </div>
            </div>

            {member.lastPaymentDate && (
              <p className="last-payment">
                Last Payment: {new Date(member.lastPaymentDate).toLocaleDateString()}
              </p>
            )}
            {member.nextDueDate && (
              <p className="next-due">
                Next Due: {new Date(member.nextDueDate).toLocaleDateString()}
              </p>
            )}
          </div>

          {/* Payment Analytics */}
          {!loadingAnalytics && paymentAnalytics && (
            <div className="payment-analytics-section">
              <h4>Payment History Summary</h4>
              <div className="analytics-grid">
                <div className="analytics-item">
                  <span className="label">Total Paid:</span>
                  <span className="value">{formatCurrency(paymentAnalytics.totalPaid)}</span>
                </div>
                <div className="analytics-item">
                  <span className="label">Payment Rate:</span>
                  <span className="value">{paymentAnalytics.paymentRate.toFixed(1)}%</span>
                </div>
                <div className="analytics-item">
                  <span className="label">On-time Payments:</span>
                  <span className="value">{paymentAnalytics.onTimePayments}</span>
                </div>
                <div className="analytics-item">
                  <span className="label">Partial Payments:</span>
                  <span className="value">{paymentAnalytics.partialPayments}</span>
                </div>
              </div>
            </div>
          )}

          {/* Payment Form */}
          <form onSubmit={handleSubmit} className="payment-form enhanced">
            <div className="form-group">
              <label htmlFor="amount">Payment Amount (₹)</label>
              <input
                type="number"
                id="amount"
                name="amount"
                value={formData.amount}
                onChange={handleInputChange}
                placeholder="Enter amount"
                step="0.01"
                min="0"
                required
              />
              <small className="form-help">
                {member.currentBalance > 0 
                  ? `Remaining balance: ${formatCurrency(member.currentBalance)}`
                  : `Full monthly fee: ${formatCurrency(member.fees)}`
                }
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="paymentType">Payment Type</label>
              <select
                id="paymentType"
                name="paymentType"
                value={formData.paymentType}
                onChange={handleInputChange}
                required
              >
                <option value="regular">Regular Payment</option>
                <option value="partial">Partial Payment</option>
                <option value="late_fee">Late Fee Payment</option>
                <option value="refund">Refund</option>
                <option value="adjustment">Adjustment</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="paymentMethod">Payment Method</label>
              <select
                id="paymentMethod"
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleInputChange}
                required
              >
                <option value="cash">Cash</option>
                <option value="online">Online Transfer</option>
                <option value="card">Card Payment</option>
                <option value="upi">UPI</option>
                <option value="cheque">Cheque</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="notes">Notes (Optional)</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Add any additional notes about this payment..."
                rows="3"
              />
            </div>

            {/* Payment Preview */}
            {formData.amount && parseFloat(formData.amount) > 0 && (
              <div className="payment-preview">
                <h4>Payment Preview</h4>
                <div className="preview-grid">
                  <div className="preview-item">
                    <span className="label">Payment Amount:</span>
                    <span className="value">{formatCurrency(formData.amount)}</span>
                  </div>
                  <div className="preview-item">
                    <span className="label">Current Balance:</span>
                    <span className="value">{formatCurrency(member.currentBalance || 0)}</span>
                  </div>
                  <div className="preview-item">
                    <span className="label">New Balance:</span>
                    <span className={`value ${Math.max(0, (member.currentBalance || 0) - parseFloat(formData.amount)) === 0 ? 'no-balance' : 'balance-due'}`}>
                      {formatCurrency(Math.max(0, (member.currentBalance || 0) - parseFloat(formData.amount)))}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <div className="form-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Recording...' : 'Record Payment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default EnhancedPaymentForm;




